/**
 * The scroll box around the canvas (10-web-admin § 6.3): `Ctrl`/`Cmd`+wheel
 * zooms around the pointer, `Space`+drag or the middle mouse button pans, a
 * plain wheel scrolls as any box does. Zoom stays 25 %–400 %; "fit" is what
 * the `0` key asks for — the whole plan inside the box.
 *
 * The canvas itself knows nothing of this: it is `zoom * 100 %` wide and the
 * box scrolls, which keeps the SVG's own matrix the single source of truth
 * for where a pointer is on the plan.
 */

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';

export const ZOOM_MIN = 0.25;
export const ZOOM_MAX = 4;
/** One wheel notch = one of the rail's steps, roughly. */
const WHEEL_FACTOR = 1.1;

export interface ViewportHandle {
  /** The `0` key: the whole plan visible, never above 100 %. */
  fit(): void;
}

export const clampZoom = (zoom: number): number => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom));

export const Viewport = forwardRef<
  ViewportHandle,
  {
    zoom: number;
    onZoom: (zoom: number) => void;
    className?: string;
    children: ReactNode;
  }
>(function Viewport({ zoom, onZoom, className, children }, ref) {
  const boxRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [panning, setPanning] = useState(false);
  const panRef = useRef<{ pointerId: number; x: number; y: number; left: number; top: number } | null>(null);

  /**
   * Zoom about a point of the box: what was under the pointer stays there.
   * The content scales with the zoom, so the scroll offset scales with it too.
   */
  const zoomTo = (next: number, anchor?: { x: number; y: number }): void => {
    const box = boxRef.current;
    const from = zoomRef.current;
    const to = clampZoom(next);
    if (!box || to === from) return;
    const point = anchor ?? { x: box.clientWidth / 2, y: box.clientHeight / 2 };
    const ratio = to / from;
    const left = (box.scrollLeft + point.x) * ratio - point.x;
    const top = (box.scrollTop + point.y) * ratio - point.y;
    onZoom(to);
    // The new size is laid out on the next frame; scroll once it exists.
    requestAnimationFrame(() => {
      box.scrollLeft = left;
      box.scrollTop = top;
    });
  };

  useImperativeHandle(ref, () => ({
    fit() {
      const box = boxRef.current;
      const content = box?.firstElementChild;
      if (!box || !content) return;
      // The content's size at the current zoom tells its size at 100 %. It is
      // an SVG, which has no `offsetWidth`; the rectangle is what it renders as.
      const rect = content.getBoundingClientRect();
      const naturalWidth = rect.width / zoomRef.current;
      const naturalHeight = rect.height / zoomRef.current;
      if (naturalWidth <= 0 || naturalHeight <= 0) return;
      const style = getComputedStyle(box);
      const innerWidth = box.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      const innerHeight = box.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
      const fitted = Math.min(1, innerWidth / naturalWidth, innerHeight / naturalHeight);
      onZoom(clampZoom(fitted));
      requestAnimationFrame(() => {
        box.scrollLeft = 0;
        box.scrollTop = 0;
      });
    },
  }));

  // Ctrl/Cmd + wheel. A React `onWheel` is passive and cannot stop the
  // browser from zooming the page, so the listener is attached by hand.
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const onWheel = (event: WheelEvent): void => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      const rect = box.getBoundingClientRect();
      const factor = event.deltaY < 0 ? WHEEL_FACTOR : 1 / WHEEL_FACTOR;
      zoomTo(zoomRef.current * factor, { x: event.clientX - rect.left, y: event.clientY - rect.top });
    };
    box.addEventListener('wheel', onWheel, { passive: false });
    return () => box.removeEventListener('wheel', onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Space is a modifier here, not a key that types anything on the canvas.
  useEffect(() => {
    const isTyping = (target: EventTarget | null): boolean =>
      target instanceof HTMLElement && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
    const down = (event: KeyboardEvent): void => {
      if (event.code !== 'Space' || isTyping(event.target)) return;
      event.preventDefault();
      setSpaceHeld(true);
    };
    const up = (event: KeyboardEvent): void => {
      if (event.code === 'Space') setSpaceHeld(false);
    };
    const blur = (): void => setSpaceHeld(false);
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', blur);
    };
  }, []);

  const onPointerDownCapture = (event: ReactPointerEvent<HTMLDivElement>): void => {
    const middle = event.button === 1;
    if (!middle && !spaceHeld) return;
    const box = boxRef.current;
    if (!box) return;
    // Ours, not the canvas's: no object drag, no placement under a pan.
    event.preventDefault();
    event.stopPropagation();
    panRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      left: box.scrollLeft,
      top: box.scrollTop,
    };
    box.setPointerCapture(event.pointerId);
    setPanning(true);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>): void => {
    const pan = panRef.current;
    const box = boxRef.current;
    if (!pan || !box || pan.pointerId !== event.pointerId) return;
    box.scrollLeft = pan.left - (event.clientX - pan.x);
    box.scrollTop = pan.top - (event.clientY - pan.y);
  };

  const endPan = (event: ReactPointerEvent<HTMLDivElement>): void => {
    if (panRef.current?.pointerId !== event.pointerId) return;
    panRef.current = null;
    setPanning(false);
  };

  return (
    <div
      ref={boxRef}
      data-testid="editor-viewport"
      className={className}
      style={{ cursor: panning ? 'grabbing' : spaceHeld ? 'grab' : undefined }}
      onPointerDownCapture={onPointerDownCapture}
      onPointerMoveCapture={onPointerMove}
      onPointerUpCapture={endPan}
      onPointerCancelCapture={endPan}
    >
      {children}
    </div>
  );
});
